import { Box, Theme, useMediaQuery } from "@mui/material"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/system"
import { SessionState, VideoPlatform } from "app-constants/constants"
import Markdown from "components/Markdown"
import AspectRatio from "components/common/AspectRatio"
import DetailsRowText from "components/common/DetailsRowText"
import SessionAdditionalResources from "components/sessions/SessionAdditionalResources"
import SessionHeader from "components/sessions/SessionHeader"
import SessionSpeakers from "components/sessions/SessionSpeakers"
import SessionTags from "components/sessions/SessionTags"
import Whereby from "components/sessions/Whereby"
import useConventionSessionRealTimeCapacity from "hooks/sessions/useConventionSessionRealTimeCapacity"
import useJoinSession from "hooks/sessions/useJoinSession"
import useScheduleSession from "hooks/sessions/useScheduleSession"
import useSessionState from "hooks/sessions/useSessionState"
import ConventionSession from "models/convention-api/v1/ConventionSession"
import React from "react"
import { pxToRem } from "utils/helpers"

interface SessionDetailsProps {
  session: ConventionSession
}

const SessionDetails = (props: SessionDetailsProps) => {
  const { session } = props
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up("md"))
  const scheduleSession = useScheduleSession(session)
  const joinSession = useJoinSession(session)

  const realTimeCapacity = useConventionSessionRealTimeCapacity(session)
  const sessionState = useSessionState({ session, joinSession, realTimeCapacity })
  const sessionDetailsText = (
    <_SessionDetailsText component="h3">Session details</_SessionDetailsText>
  )
  const detailsRowText = (
    <_DetailsRowText
      startAt={session.start_at}
      endAt={session.end_at}
      platform={session.video_platform}
    />
  )
  const description = <_Description allow={["emphasis"]}>{session.description}</_Description>
  const sessionSpeakers = session?.assignments?.length ? (
    <_SessionSpeakers session={session} />
  ) : null
  const sessionAdditionalResources = session?.attachments?.length ? (
    <_SessionAdditionalResources session={session} />
  ) : null
  const sessionTags = session?.taggings?.length ? <_SessionTags session={session} /> : null

  return (
    <_Container>
      {sessionState === SessionState.JOINED && session.video_platform === VideoPlatform.WHEREBY ? (
        <_WherebyContainer ratio={69 / 41}>
          <Whereby meetingUrl={session.integrated_meeting_url} />
        </_WherebyContainer>
      ) : (
        <SessionHeader
          session={session}
          scheduleSession={scheduleSession}
          joinSession={joinSession}
          sessionState={sessionState}
        />
      )}
      {isDesktop ? (
        <_DetailsContainerDesktop>
          <_DetailsContainerDesktopLeftColumn>
            {sessionDetailsText}
            {detailsRowText}
            {description}
            {sessionSpeakers}
          </_DetailsContainerDesktopLeftColumn>
          <_DetailsContainerDesktopRightColumn>
            {sessionAdditionalResources}
            {sessionTags}
          </_DetailsContainerDesktopRightColumn>
        </_DetailsContainerDesktop>
      ) : (
        <_DetailsContainerMobile>
          {sessionDetailsText}
          {detailsRowText}
          {description}
          {sessionSpeakers}
          {sessionAdditionalResources}
          {sessionTags}
        </_DetailsContainerMobile>
      )}
    </_Container>
  )
}

export default SessionDetails

const _Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flexFlow: "column nowrap",
  gap: pxToRem(30),
  width: '100%',
  height: "100%",
  backgroundColor: theme.palette.gray.lightest,
  margin: pxToRem(20),
  zIndex: 1,
  [theme.breakpoints.up("md")]: {
    borderRadius: pxToRem(10),
    margin: pxToRem(40),
    padding: pxToRem(20),
    overflowY: "hidden",
  },
}))

const _DetailsContainerMobile = styled(Box)({
  display: "grid",
  gridGap: "30px",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  gridTemplateColumns: "1fr",
})

const _DetailsContainerDesktop = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexFlow: "nowrap row",
  gap: "130px",
})

const _DetailsContainerDesktopLeftColumn = styled(Box)({
  display: "flex",
  gap: "30px",
  flexFlow: "nowrap column",
  flex: 1,
})

const _DetailsContainerDesktopRightColumn = styled(Box)({
  display: "flex",
  gap: "30px",
  flexFlow: "nowrap column",
  width: "280px",
})

const _DetailsRowText = styled(DetailsRowText)({}) as typeof DetailsRowText

const _SessionDetailsText = styled(Typography)(({ theme }) => ({
  ...theme.fonts.lato400,
  fontSize: "20px",
  lineHeight: "24px",
})) as typeof Typography

const _Description = styled(Markdown)({}) as typeof Markdown

const _SessionSpeakers = styled(SessionSpeakers)({}) as typeof SessionSpeakers

const _SessionAdditionalResources = styled(SessionAdditionalResources)(
  {},
) as typeof SessionAdditionalResources

const _SessionTags = styled(SessionTags)({}) as typeof SessionTags

const _WherebyContainer = styled(AspectRatio)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  height: "100%",
  borderRadius: pxToRem(10),
  overflow: "hidden",
}) as typeof AspectRatio
